import { useSetTaskDone } from '../../lib/data';
import { PRIORITIES, TASK_STAGES, type BoardLead, type Client, type Task } from '../../lib/types';
import { Avatar, DueText, useDrawer, useToast } from '../../ui/kit';

/** One task: tick, title with client and lead, priority, stage, owner, deadline. */
export function TaskLine({
  task,
  client,
  lead,
}: {
  task: Task;
  client?: Client | undefined;
  lead?: BoardLead | undefined;
}) {
  const { open } = useDrawer();
  const toast = useToast();
  const setDone = useSetTaskDone();
  const done = Boolean(task.done_at);
  const prio = PRIORITIES.find((p) => p.id === task.priority)!;
  const stage = TASK_STAGES.find((s) => s.id === task.stage)!;

  function toggle() {
    setDone.mutate(
      { task: task.id, done: !done },
      {
        onSuccess: () => {
          if (!done) toast('Task done', () => setDone.mutate({ task: task.id, done: false }));
        },
        onError: (e) => toast(e.message),
      },
    );
  }

  return (
    <div className={`tkrow ${done ? 'done' : ''}`}>
      <button
        className={`check ${done ? 'on' : ''}`}
        aria-label={done ? 'Mark as not done' : 'Mark as done'}
        onClick={toggle}
        disabled={setDone.isPending}
      />
      <button className="tkmain" onClick={() => open({ type: 'task', id: task.id })}>
        <span className="t">{task.text}</span>
        <span className="m">
          {client && (
            <span className="cchip">
              <i style={{ background: client.colour }} aria-hidden />
              {client.name}
            </span>
          )}
          {lead && <span>{lead.clinic}</span>}
          {task.notes && <span aria-label="Has notes">✎</span>}
          {/* On a phone the pills on the right are hidden; the same facts go here. */}
          {task.priority !== 'normal' && (
            <span className={`mo pt-${task.priority}`}>{prio.label}</span>
          )}
          {task.stage !== 'todo' && task.stage !== 'done' && (
            <span className="mo">{stage.label}</span>
          )}
        </span>
      </button>
      {task.priority !== 'normal' && <span className={`pill sm ${prio.cls}`}>{prio.label}</span>}
      {task.stage !== 'todo' && task.stage !== 'done' && (
        <span className="pill sm">{stage.label}</span>
      )}
      <Avatar id={task.owner_id} small />
      {task.co_owner_id && <Avatar id={task.co_owner_id} small />}
      <DueText due={task.due_at} done={done} />
    </div>
  );
}
